// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useMemo, useState } from 'react';

import type { PlayingStyle } from './types';

type Options = {
  apiBaseUrl: string;
  token: string | null | undefined;
};

export type PlayingStyleErrorCode = 'noPlayer' | 'loadFailed';

type Result = {
  playingStyle: PlayingStyle | null;
  loading: boolean;
  error: PlayingStyleErrorCode | null;
  reload: () => void;
};

/** Loads the signed-in player's playing style, measured from every hole they have scored. */
export function usePlayingStyle({ apiBaseUrl, token }: Options): Result {
  const [playingStyle, setPlayingStyle] = useState<PlayingStyle | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<PlayingStyleErrorCode | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const reload = useCallback(() => setReloadToken((value) => value + 1), []);

  const headers = useMemo(
    () => (token ? { Authorization: `Bearer ${token}` } : undefined),
    [token]
  );

  useEffect(() => {
    if (!token) {
      setPlayingStyle(null);
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    setLoading(true);
    setError(null);

    fetch(`${apiBaseUrl}/api/player/playing-style`, { headers, signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(String(response.status));
        }
        return (await response.json()) as PlayingStyle;
      })
      .then((payload) => {
        if (controller.signal.aborted) return;
        setPlayingStyle(payload);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setPlayingStyle(null);
        setError(err instanceof Error && err.message === '404' ? 'noPlayer' : 'loadFailed');
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      });

    return () => controller.abort();
  }, [apiBaseUrl, headers, reloadToken, token]);

  return { playingStyle, loading, error, reload };
}
